// Copyright (C) 2024 Browser Video Implementation for Cloudflare Streaming

const React = require('react');
const PropTypes = require('prop-types');
const browserStreamingService = require('stremio/common/browser-streaming');
const EventEmitter = require('eventemitter3');

class BrowserVideo {
    constructor() {
        this.events = new EventEmitter();
        this.videoElement = null;
        this.containerElement = null;
        this.state = {
            paused: true,
            time: 0,
            duration: 0,
            buffering: false,
            buffered: [],
            volume: 1,
            muted: false,
            playbackSpeed: 1,
            audioTracks: [],
            selectedAudioTrackId: null,
            subtitlesTracks: [],
            selectedSubtitlesTrackId: null,
            manifest: null,
            stream: null
        };
        this.props = [
            'paused', 'time', 'duration', 'buffering', 'buffered', 
            'volume', 'muted', 'playbackSpeed', 'audioTracks', 
            'selectedAudioTrackId', 'subtitlesTracks', 'selectedSubtitlesTrackId'
        ];
    }

    on(event, handler) {
        this.events.on(event, handler);
    }

    off(event, handler) {
        this.events.off(event, handler);
    }

    dispatch(action, options = {}) {
        try {
            switch (action.type) {
                case 'command':
                    this.handleCommand(action, options);
                    break;
                case 'setProp':
                    this.setProp(action.propName, action.propValue);
                    break;
                case 'observeProp':
                    this.observeProp(action.propName);
                    break;
            }
        } catch (error) {
            console.error('BrowserVideo dispatch error:', error);
            this.events.emit('error', error);
        }
    }

    handleCommand(action, options) {
        switch (action.commandName) {
            case 'load':
                this.load(action.commandArgs, options);
                break;
            case 'unload':
                this.unload();
                break;
            case 'addExtraSubtitlesTracks':
                this.addExtraSubtitlesTracks(action.commandArgs.tracks);
                break;
            case 'addLocalSubtitles':
                this.addLocalSubtitles(action.commandArgs.filename, action.commandArgs.buffer);
                break;
        }
    }

    async load(args, options) {
        try {
            console.log('BrowserVideo loading stream:', args);
            
            if (!options.containerElement) {
                throw new Error('Container element is required');
            }

            this.containerElement = options.containerElement;
            
            // Create video element if it doesn't exist
            if (!this.videoElement) {
                this.videoElement = document.createElement('video');
                this.videoElement.style.width = '100%';
                this.videoElement.style.height = '100%';
                this.videoElement.style.objectFit = 'contain';
                this.videoElement.controls = false;
                this.videoElement.crossOrigin = 'anonymous';
                
                // Set up event listeners
                this.setupVideoEventListeners();
                
                this.containerElement.appendChild(this.videoElement);
            }

            // Check if we have a stream to play
            console.log('Stream object structure:', args.stream);
            console.log('Load arguments:', args);
            
            // Extract video URL from different possible stream formats
            let videoUrl = null;
            
            if (args.stream) {
                // Try different possible URL locations
                if (args.stream.behaviorHints && args.stream.behaviorHints.videoUrl) {
                    videoUrl = args.stream.behaviorHints.videoUrl;
                } else if (args.stream.url) {
                    videoUrl = args.stream.url;
                } else if (args.streamingServerURL) {
                    // Use the streaming server URL from Player component
                    videoUrl = args.streamingServerURL;
                    console.log('Using streaming server URL from Player:', videoUrl);
                } else if (args.stream.infoHash) {
                    // This is a torrent stream - we need to use the streaming server
                    console.log('Torrent stream detected, delegating to streaming server wrapper');
                    // Instead of throwing, silently delegate by setting no source
                    this.videoElement.src = '';
                    this.videoElement.removeAttribute('src');
                    return;
                } else {
                    console.log('No video URL found in stream, checking streaming server');
                    throw new Error('No video URL found in stream - using streaming server wrapper');
                }
            }
            
            if (videoUrl) {
                
                // Use browser streaming service
                if (browserStreamingService.enableBrowserStreaming) {
                    console.log('Using browser streaming service for:', videoUrl);
                    
                    const result = await browserStreamingService.streamVideo(videoUrl, this.videoElement);
                    
                    if (result.success) {
                        this.state.stream = args.stream;
                        this.state.manifest = {
                            name: 'Browser Video',
                            props: this.props
                        };
                        
                        this.events.emit('implementationChanged', this.state.manifest);
                        
                        // Start playback only if we have a valid source
                        if (args.autoplay && this.videoElement.src && this.videoElement.src !== '') {
                            this.videoElement.play().catch(error => {
                                console.error('Autoplay failed:', error);
                                // Don't throw error, just log it - user can manually start playback
                                console.log('Autoplay blocked - user interaction required to start video');
                            });
                        }
                        
                        console.log('Browser streaming started successfully');
                    } else {
                        if (result.shouldDelegateToWrapper) {
                            console.log('Browser streaming cannot handle this URL, delegating to streaming server wrapper');
                            // Instead of throwing, silently delegate by setting no source
                            this.videoElement.src = '';
                            this.videoElement.removeAttribute('src');
                            return;
                        } else {
                            console.log('Browser streaming failed, falling back to direct URL');
                            // Direct video source
                            this.videoElement.src = videoUrl;
                            this.videoElement.crossOrigin = 'anonymous';
                            
                            if (args.autoplay) {
                                this.videoElement.play().catch(error => {
                                    console.error('Autoplay failed:', error);
                                });
                            }
                        }
                    }
                } else {
                    // Browser streaming is disabled (cloud deployment)
                    console.log('Browser streaming disabled for cloud deployment, using direct video source');
                    
                    // For cloud deployment, use direct video source with proper CORS handling
                    this.videoElement.src = videoUrl;
                    this.videoElement.crossOrigin = 'anonymous';
                    
                    // Set up basic video element event handlers
                    this.setupVideoEventHandlers();
                    
                    if (args.autoplay) {
                        this.videoElement.play().catch(error => {
                            console.error('Autoplay failed:', error);
                            console.log('User interaction may be required to start video playback');
                        });
                    }
                    
                    // Emit implementation changed event for cloud deployment
                    this.state.stream = args.stream;
                    this.state.manifest = {
                        name: 'Cloud Video',
                        props: this.props
                    };
                    
                    this.events.emit('implementationChanged', this.state.manifest);
                }
            } else {
                // Let the streaming server wrapper handle this case
            console.log('No video URL found, delegating to streaming server wrapper');
            // Instead of throwing, silently delegate by setting no source
            this.videoElement.src = '';
            this.videoElement.removeAttribute('src');
            return;
            }
            
        } catch (error) {
            console.error('BrowserVideo load error:', error);
            // Handle errors gracefully - don't throw, just log and continue
            // The streaming server wrapper will handle the fallback
            this.videoElement.src = '';
            this.videoElement.removeAttribute('src');
        }
    }

    // Set up basic video event handlers for cloud deployment
    setupVideoEventHandlers() {
        if (!this.videoElement) return;

        // Remove existing handlers to prevent duplicates
        this.removeVideoEventHandlers();

        // Error handler
        this.videoErrorHandler = (e) => {
            console.error('Video element error:', e);
            console.log('Video error details:', {
                code: this.videoElement.error?.code,
                message: this.videoElement.error?.message,
                readyState: this.videoElement.readyState,
                networkState: this.videoElement.networkState
            });
        };
        this.videoElement.addEventListener('error', this.videoErrorHandler);

        // Load handler
        this.videoLoadHandler = () => {
            console.log('Video loaded successfully');
        };
        this.videoElement.addEventListener('load', this.videoLoadHandler);

        // Can play handler
        this.videoCanPlayHandler = () => {
            console.log('Video can play');
        };
        this.videoElement.addEventListener('canplay', this.videoCanPlayHandler);

        console.log('Video event handlers set up for cloud deployment');
    }

    // Remove video event handlers
    removeVideoEventHandlers() {
        if (!this.videoElement) return;

        if (this.videoErrorHandler) {
            this.videoElement.removeEventListener('error', this.videoErrorHandler);
            this.videoErrorHandler = null;
        }
        if (this.videoLoadHandler) {
            this.videoElement.removeEventListener('load', this.videoLoadHandler);
            this.videoLoadHandler = null;
        }
        if (this.videoCanPlayHandler) {
            this.videoElement.removeEventListener('canplay', this.videoCanPlayHandler);
            this.videoCanPlayHandler = null;
        }
    }

    setupVideoEventListeners() {
        if (!this.videoElement) return;

        // Add comprehensive error handling
        this.videoElement.addEventListener('error', (e) => {
            console.error('Video element error:', e);
            console.log('Video error details:', {
                code: this.videoElement.error?.code,
                message: this.videoElement.error?.message,
                readyState: this.videoElement.readyState,
                networkState: this.videoElement.networkState,
                src: this.videoElement.src
            });
            
            // Don't emit error event that would break the player
            // Just log it and let the user try a different approach
        });

        this.videoElement.addEventListener('loadedmetadata', () => {
            this.state.duration = this.videoElement.duration;
            this.events.emit('propChanged', 'duration', this.state.duration);
        });

        this.videoElement.addEventListener('timeupdate', () => {
            this.state.time = this.videoElement.currentTime;
            this.events.emit('propChanged', 'time', this.state.time);
        });

        this.videoElement.addEventListener('play', () => {
            this.state.paused = false;
            this.events.emit('propChanged', 'paused', this.state.paused);
        });

        this.videoElement.addEventListener('pause', () => {
            this.state.paused = true;
            this.events.emit('propChanged', 'paused', this.state.paused);
        });

        this.videoElement.addEventListener('ended', () => {
            this.events.emit('ended');
        });

        this.videoElement.addEventListener('volumechange', () => {
            this.state.volume = this.videoElement.volume;
            this.state.muted = this.videoElement.muted;
            this.events.emit('propChanged', 'volume', this.state.volume);
            this.events.emit('propChanged', 'muted', this.state.muted);
        });

        this.videoElement.addEventListener('progress', () => {
            const buffered = [];
            for (let i = 0; i < this.videoElement.buffered.length; i++) {
                buffered.push({
                    start: this.videoElement.buffered.start(i),
                    end: this.videoElement.buffered.end(i)
                });
            }
            this.state.buffered = buffered;
            this.events.emit('propChanged', 'buffered', this.state.buffered);
        });

        this.videoElement.addEventListener('waiting', () => {
            this.state.buffering = true;
            this.events.emit('propChanged', 'buffering', this.state.buffering);
        });

        this.videoElement.addEventListener('playing', () => {
            this.state.buffering = false;
            this.events.emit('propChanged', 'buffering', this.state.buffering);
        });

        this.videoElement.addEventListener('ratechange', () => {
            this.state.playbackSpeed = this.videoElement.playbackRate;
            this.events.emit('propChanged', 'playbackSpeed', this.state.playbackSpeed);
        });
    }

    unload() {
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.src = '';
            this.videoElement.removeAttribute('src');
            this.videoElement.load();
        }
        
        browserStreamingService.cleanup();
        
        this.state = {
            paused: true,
            time: 0,
            duration: 0,
            buffering: false,
            buffered: [],
            volume: 1,
            muted: false,
            playbackSpeed: 1,
            audioTracks: [],
            selectedAudioTrackId: null,
            subtitlesTracks: [],
            selectedSubtitlesTrackId: null,
            manifest: null,
            stream: null
        };
    }

    setProp(name, value) {
        if (this.videoElement) {
            switch (name) {
                case 'paused':
                    if (value) {
                        this.videoElement.pause();
                    } else {
                        // Check if video has a valid source before trying to play
                        if (this.videoElement.src && this.videoElement.src !== '' && this.videoElement.src !== 'about:blank') {
                            this.videoElement.play().catch((error) => {
                                console.error('Play failed:', error);
                                // Check if this is due to no supported source
                                if (error.name === 'NotSupportedError') {
                                    console.log('Video element has no supported sources. This may be due to:');
                                    console.log('- Local streaming server not running (for torrents)');
                                    console.log('- Invalid video URL or format');
                                    console.log('- Network issues preventing video load');
                                }
                            });
                        } else {
                            console.log('Cannot play: video element has no valid source set - delegating to streaming server wrapper');
                            // This is expected for local streaming server URLs - don't treat as error
                        }
                    }
                    break;
                case 'volume':
                    this.videoElement.volume = value;
                    break;
                case 'muted':
                    this.videoElement.muted = value;
                    break;
                case 'playbackSpeed':
                    this.videoElement.playbackRate = value;
                    break;
                case 'time':
                    this.videoElement.currentTime = value;
                    break;
            }
        }
        
        this.state[name] = value;
    }

    observeProp(name) {
        // Emit current value for observed property
        if (this.state[name] !== undefined) {
            this.events.emit('propValue', name, this.state[name]);
        }
    }

    addExtraSubtitlesTracks(tracks) {
        // Store subtitles tracks
        this.state.subtitlesTracks = tracks;
        this.events.emit('propChanged', 'subtitlesTracks', this.state.subtitlesTracks);
    }

    addLocalSubtitles(filename, buffer) {
        // Handle local subtitles
        console.log('Adding local subtitles:', filename);
        // This would need additional implementation for subtitle parsing
    }

    destroy() {
        console.log('Destroying BrowserVideo component');
        
        // Clean up video element
        if (this.videoElement) {
            // Remove all event listeners
            this.videoElement.removeEventListener('loadedmetadata', this.onLoadedMetadata);
            this.videoElement.removeEventListener('timeupdate', this.onTimeUpdate);
            this.videoElement.removeEventListener('progress', this.onProgress);
            this.videoElement.removeEventListener('waiting', this.onWaiting);
            this.videoElement.removeEventListener('canplay', this.onCanPlay);
            this.videoElement.removeEventListener('error', this.onError);
            this.videoElement.removeEventListener('ended', this.onEnded);
            this.videoElement.removeEventListener('play', this.onPlay);
            this.videoElement.removeEventListener('pause', this.onPause);
            this.videoElement.removeEventListener('volumechange', this.onVolumeChange);
            this.videoElement.removeEventListener('ratechange', this.onRateChange);
            
            // Remove cloud deployment event handlers
            this.removeVideoEventHandlers();
            
            // Clear video source
            this.videoElement.src = '';
            this.videoElement.removeAttribute('src');
            this.videoElement.load();
        }
        
        // Clean up browser streaming service
        if (browserStreamingService) {
            browserStreamingService.cleanup();
        }
        
        this.unload();
        if (this.videoElement && this.containerElement) {
            this.containerElement.removeChild(this.videoElement);
        }
        this.events.removeAllListeners();
        
        console.log('BrowserVideo component destroyed');
    }
}

module.exports = BrowserVideo;